import { derived, writable } from "svelte/store";
import type {
  ModalStoryblok,
  PageStoryblok,
  NewsroomLandingStoryblok,
} from "$types/components";
import Fuse from "fuse.js";
import type { ThemeBackgroundColor } from "$lib/utils/helpers";

export const pageMainColor = writable<string>("grey");
export const pageMainColorUsage = writable<string>("hero-footer");
export const heroBackgroundColor = writable<ThemeBackgroundColor>("none");
export const headerVariant = writable<string>("default");
export const headerHidden = writable<boolean>(false);
export const hideHeader = writable<boolean>(false);
export const page = writable<PageStoryblok | NewsroomLandingStoryblok>();
export const story = writable<any>();
export const modalIsOpened = writable<boolean>(false);
export const modalContent = writable<ModalStoryblok | ModalStoryblok[] | null>(
  null,
);
export const modalStartIndex = writable<number>(0);
export const menuIsOpen = writable<boolean>(false);
export const videoIsPlaying = writable<any>(false);
export const pageTransitionOut = writable<boolean>(false);
export const transitionState = writable<"in" | "out" | "initial" | null>(null);
export const textDir = writable<"ltr" | "rtl">("ltr");
export const logoWidth = writable<any>(null);
export const lazyloadImg = writable<any>(null);

export const formInitialValues = writable<any>(null);

export type BlogFilter = {
  field: string;
  filter: string;
};
export const blogFilter = writable<BlogFilter>({ field: "", filter: "" });

export const theme = derived(pageMainColor, (value) => {
  if (value === "green") {
    return "dark";
  }
  return "light";
});

export const careersMobileFiltersOpen = writable<boolean>(false);

export const blockScroll = derived(
  [modalIsOpened, menuIsOpen, careersMobileFiltersOpen, transitionState],
  ([$pauseScroll, $menuOpen, $careersMobileFiltersOpen, $isTransitioning]) => {
    return (
      $pauseScroll ||
      $menuOpen ||
      $careersMobileFiltersOpen ||
      $isTransitioning === "in"
    );
  },
);

// Careers

export const careersMapItem = writable<any>();

export const jobs = writable<any>([]);

export const departments = derived([jobs], ([$jobs]) => {
  return [
    ...new Set(
      $jobs.map((j) => {
        return j.departments.length > 0 ? j.departments[0].name : null;
      }),
    ),
  ];
});

export const locations = derived([jobs], ([$jobs]) => {
  function arrayToNestedObject(inputArray: string[]) {
    const outputObject = {};

    inputArray.forEach((inputString) => {
      let currentObject = outputObject;

      const splitStrings = inputString
        .split(",")
        .map((str) => str.trim())
        .reverse();

      splitStrings.forEach((splitString, i) => {
        if (!currentObject[splitString]) {
          currentObject[splitString] = {};
        }
        currentObject = currentObject[splitString];
      });
    });

    return outputObject;
  }

  const locationsObj = arrayToNestedObject([
    ...new Set(
      $jobs.map((j) =>
        j?.location?.name?.length > 0 ? j?.location?.name : null,
      ),
    ),
  ]);

  return Object.keys(locationsObj).map((first) => {
    const children = Object.keys(locationsObj[first]);
    return {
      name: first,
      full: first,
      children: children.map((second) => {
        const secondChildren = Object.keys(locationsObj[first][second]);
        return {
          name: second,
          full: [first, second].reverse().join(", "),
          children: secondChildren.map((third) => {
            return {
              name: third,
              full: [first, second, third].reverse().join(", "),
              children: null,
            };
          }),
        };
      }),
    };
  });
});

export const selectedCareersLocations = writable<any>([]);
export const selectedCareersDepartments = writable<any>([]);
export const jobsSearch = writable<any>("");

export const filteredJobs = derived(
  [jobs, selectedCareersLocations, selectedCareersDepartments, jobsSearch],
  ([
    $jobs,
    $selectedCareersLocations,
    $selectedCareersDepartments,
    $jobsSearch,
  ]) => {
    $jobs = JSON.parse(JSON.stringify($jobs));

    if ($jobsSearch.trim().length > 0) {
      const options = {
        keys: ["title", "content"],
      };
      const fuse = new Fuse($jobs, options);
      const result = fuse.search($jobsSearch).map(({ item }) => item);
      $jobs = result;
    }

    if ($selectedCareersDepartments.length > 0) {
      $jobs = $jobs.filter(
        (job) =>
          job.departments.filter((d) =>
            $selectedCareersDepartments.includes(d.name),
          ).length > 0,
      );
    }

    if ($selectedCareersLocations.length > 0) {
      $jobs = $jobs.filter((job) => {
        const splitReverse = job.location.name.split(", ").reverse();
        let isMatch = false;
        const matchIndex = $selectedCareersLocations
          .map((l) => l.name)
          .indexOf(splitReverse[0]);
        if (matchIndex !== -1) {
          isMatch = true;

          if ($selectedCareersLocations[matchIndex].children.length > 0) {
            const childNames = $selectedCareersLocations[
              matchIndex
            ].children.map((c) => c.name);
            const childIndex = childNames.indexOf(splitReverse[1]);
            isMatch = childIndex !== -1;

            if (
              isMatch &&
              $selectedCareersLocations[matchIndex].children[childIndex]
                .children.length > 0
            ) {
              const subChildNames = $selectedCareersLocations[
                matchIndex
              ].children[childIndex].children.map((c) => c.name);
              isMatch = subChildNames.includes(splitReverse[2]);
            }
          }
        }

        return isMatch;
      });
    }

    return $jobs;
  },
);
